import React, { useState } from "react";
import ReactModal from "react-modal";
import { useQuery } from "@tanstack/react-query";
import { get } from "@utils/axios_api";
import "./UserManualModal.css";

const ROLE_BADGE_CLASS = {
  R0: "badge-r0",
  R1: "badge-r1",
  R2: "badge-r2",
  R3: "badge-r3",
};

function UserManualModal({ isOpen, onClose }) {
  const [sectionIdx, setSectionIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const { data: sections = [], isLoading } = useQuery({
    queryKey: ["manualSlides"],
    queryFn: () => get("/user/manual/slides").then((r) => r.result ?? []),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });

  const section = sections[sectionIdx];
  const slides = section?.slides ?? [];
  const slide = slides[slideIdx];
  const total = slides.length;

  const goSection = (idx) => {
    setSectionIdx(idx);
    setSlideIdx(0);
  };

  const goSlide = (sIdx, slIdx) => {
    setSectionIdx(sIdx);
    setSlideIdx(slIdx);
  };

  const goPrev = () => setSlideIdx((i) => Math.max(0, i - 1));
  const goNext = () => setSlideIdx((i) => Math.min(total - 1, i + 1));

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={onClose}
        overlayClassName="modal-overlay"
        className="modal-wrap"
      >
        <div className="modal-container um-container">
          <div className="modal-header">
            <h5>사용자 설명서</h5>
            <button type="button" className="modal-btn-close" onClick={onClose}>
              <span className="modal-btn-span">X</span>
            </button>
          </div>

          <div className="um-body">
            {isLoading ? (
              <div className="um-loading">로딩 중...</div>
            ) : (
              <>
                {/* 좌측 사이드바 */}
                <div className="um-tab-list">
                  {sections.map((sec, sIdx) => {
                    const isActive = sectionIdx === sIdx;
                    return (
                      <div key={sIdx}>
                        {/* 섹션 헤더 */}
                        <button
                          className={`um-tab-item${isActive ? " um-tab-active" : ""}`}
                          onClick={() => goSection(sIdx)}
                        >
                          <div className="um-tab-row">
                            <span className="um-tab-id">
                              {String(sIdx + 1).padStart(2, "0")}
                            </span>
                            <span className="um-tab-title">{sec.sectionTitle}</span>
                            <span className="um-tab-arrow">{isActive ? "▾" : "▸"}</span>
                          </div>
                        </button>

                        {/* 슬라이드 서브 목록 */}
                        {isActive && (
                          <div className="um-sub-list">
                            {(sec.slides ?? []).map((sl, slIdx) => (
                              <button
                                key={slIdx}
                                className={`um-sub-item${slideIdx === slIdx ? " um-sub-active" : ""}`}
                                onClick={() => goSlide(sIdx, slIdx)}
                              >
                                <span className="um-sub-num">{slIdx + 1}</span>
                                <span className="um-sub-title">{sl.title}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* 우측 슬라이드 뷰어 */}
                <div className="um-viewer">
                  {slide && (
                    <>
                      <div className="um-image-wrap">
                        {slide.imagePath ? (
                          <img
                            src={slide.imagePath}
                            alt={slide.title}
                            className="um-slide-img"
                            onClick={() => setLightbox(slide.imagePath)}
                            title="클릭하면 크게 볼 수 있습니다"
                          />
                        ) : (
                          <div className="um-no-image">이미지 없음</div>
                        )}
                      </div>

                      <div className="um-slide-info">
                        <div className="um-slide-header">
                          <span className="um-slide-title">{slide.title}</span>
                          <div className="um-badge-list">
                            {(slide.roles ?? "")
                              .split(",")
                              .filter(Boolean)
                              .map((role) => (
                                <span
                                  key={role}
                                  className={`um-badge ${ROLE_BADGE_CLASS[role] ?? ""}`}
                                >
                                  {role}
                                </span>
                              ))}
                          </div>
                        </div>
                        <ul className="um-slide-items">
                          {(slide.items ?? []).map((item, j) => (
                            <li key={j}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="um-nav">
                        <button
                          className="um-nav-btn"
                          onClick={goPrev}
                          disabled={slideIdx === 0}
                        >
                          ◀ 이전
                        </button>
                        <span className="um-nav-counter">
                          {slideIdx + 1} / {total}
                        </span>
                        <button
                          className="um-nav-btn"
                          onClick={goNext}
                          disabled={slideIdx === total - 1}
                        >
                          다음 ▶
                        </button>
                      </div>

                      {total > 1 && (
                        <div className="um-dots">
                          {slides.map((_, i) => (
                            <button
                              key={i}
                              className={`um-dot${i === slideIdx ? " um-dot-active" : ""}`}
                              onClick={() => setSlideIdx(i)}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </ReactModal>

      {lightbox && (
        <div className="um-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="확대 보기" />
          <button className="um-lightbox-close" onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </>
  );
}

export { UserManualModal };
